import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as Calender } from '../../../../assets/icons/calender.svg';
import { ReactComponent as ClockIcon } from '../../../../assets/icons/clock-icon.svg';
import { ReactComponent as PowerConsume } from '../../../../assets/icons/power-consume.svg';
import BackGroundImage from '../../../../assets/images/background1.png';
import NoDataFound from '../../../../assets/images/no-data-found.png';
import { useAppBranding } from '../../../../config/branding';
import { chartTimePeriod } from '../../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchStationDetailsData,
  removeStationDetails,
} from '../../../../redux/slices/StationDetailsSlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import ConvertUTCtoHHMM from '../../../../shared-functions/ConvertUTCtoHHMM';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import stationManagementApi from '../../../../utils/api/station-management';
import SelectAmenities from '../../../charging-station/components/SelectAmenities';

interface ToggleDataProperties {
  id: number;
  icon: React.ElementType;
  label: string | number;
  desc: string;
}

/**
 * Station details component
 * @returns {any} The rendered component
 */
function StationDetails() {
  const branding = useAppBranding();
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'media' | 'amenities'>('media');
  const [chartData, setChartData] = useState<any>(null);
  // const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isChartLoading, setIsChartLoading] = useState<boolean>(false);
  const { stationDetails, isLoading } = useAppSelector((state) => state.stationDetails);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);

  const activeRangeReference = useRef<'last24hours' | 'last7days' | 'lastMonth' | 'last12months'>(
    'last24hours',
  );

  const fetchStationDetailsChartData = useCallback(
    async (range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months') => {
      activeRangeReference.current = range;
      setIsChartLoading(true);

      try {
        const response = await stationManagementApi.fetchStationChartData({
          timePeriod: range,
          chargingStation: selectedChargingPoint?.id,
        });

        if (activeRangeReference.current !== range) return; // Ignore outdated responses
        if (response.status !== 200) throw new Error('Invalid response');

        const result = response?.data;
        setChartData(result);
      } catch (error) {
        console.error('Failed to fetch station details:', error);
      } finally {
        if (activeRangeReference.current === range) setIsChartLoading(false);
      }
    },
    [selectedChargingPoint?.id],
  );

  const toggleData = useMemo<ToggleDataProperties[]>(() => {
    if (!chartData) return [];
    const value = ToFixedValue(Number(chartData?.totalPowerConsumed) / 1000);
    return [
      {
        id: 1,
        icon: branding.icons.currencyIcon,
        label: `${ToFixedValue(chartData.totalRevenue)} ${branding.text.currency}`,
        desc: t('totalRevenue'),
      },
      {
        id: 2,
        icon: ClockIcon,
        label: chartData.totalTransactionDurationInHours
          ? chartData.totalTransactionDurationInHours.replace('Hr', t('hr'))
          : `0 ${t('hr')}`,
        desc: t('noOfHoursUsed'),
      },
      {
        id: 3,
        icon: PowerConsume,
        label: `${value ?? 0} ${t('kW')}`,
        desc: t('totalPowerConsumed'),
      },
      {
        id: 4,
        icon: Calender,
        label: `${chartData.totalScheduledSessions ?? 0}`,
        desc: t('totalScheduledCharges'),
      },
    ];
  }, [chartData, t]);

  /**
   * Fetch the account details in redus
   */
  const fetchData = () => {
    if (selectedChargingPoint?.id) {
      dispatch(fetchStationDetailsData(selectedChargingPoint.id));
    }
  };

  useEffect(() => {
    if (stationDetails && stationDetails?.submission?.status !== 'approved') {
      dispatch(removeStationDetails());
      navigate('/ev-admin/charging-station/approved');
    }
    if (selectedChargingPoint?.id) {
      fetchStationDetailsChartData('last24hours');
      fetchData();
    }
  }, [selectedChargingPoint]);

  return (
    <div className="station-details-wrap" dir={i18n.dir()}>
      {/* LEFT SECTION */}
      <div className="station-details-left">
        <div className="left-top">
          <div className="left-container">
            {(() => {
              // default
              let stationDetail = null;

              if (isLoading) {
                stationDetail = (
                  <div className="skeleton-grid-loader">
                    <div className="skeleton-card" />
                  </div>
                );
              }
              // stationDetails exists and has data
              else if (stationDetails) {
                stationDetail = (
                  <div className="left">
                    <div className="info">
                      <p>{t('stationName')} :</p>
                      <p>{stationDetails.name}</p>
                    </div>
                    <div className="info">
                      <p>{t('address')} :</p>
                      <p>{stationDetails.address}</p>
                    </div>
                    <div className="info">
                      <p>{t('availability')} :</p>
                      <p>{stationDetails.isAvailable ? t('available') : t('unavailable')}</p>
                    </div>
                    <div className="info">
                      <p>{t('electricityCost')} :</p>
                      <p>{stationDetails.electricityCostByUtilityProvider}</p>
                    </div>
                    <div className="info">
                      <p>{t('operatingHrs')} :</p>
                      <p>
                        {`${ConvertUTCtoHHMM(stationDetails.opensAt)} ${
                          Number(ConvertUTCtoHHMM(stationDetails.opensAt).split(':')[0]) >= 12
                            ? t('pm')
                            : t('am')
                        }`}
                        -{' '}
                        {`${ConvertUTCtoHHMM(stationDetails.closesAt)} ${
                          Number(ConvertUTCtoHHMM(stationDetails.closesAt).split(':')[0]) >= 12
                            ? t('pm')
                            : t('am')
                        }`}
                      </p>
                    </div>
                  </div>
                );
              }
              // stationDetails exists but has no data
              else {
                stationDetail = (
                  <div className="table-no-data-wrap">
                    <img src={NoDataFound} alt="" className="no-data-found-image" />
                    <div className="no-data-found-details">
                      <h3>{t('noDataTitle')}</h3>
                      <p>{t('noDataDescription')}</p>
                    </div>
                  </div>
                );
              }

              return stationDetail;
            })()}
          </div>
        </div>

        <div className="station-details-vertical-line" />

        <div className="left-bottom">
          <CustomToggleButton
            labels={chartTimePeriod.map(({ value }) => t(value))}
            onToggle={(activeIndex) => {
              const selected = chartTimePeriod[activeIndex]?.value ?? 'last24hours';
              fetchStationDetailsChartData(selected);
            }}
            defaultActiveIndex={0}
            type="chart"
          />

          {isChartLoading ? (
            <div className="skeleton-grid-loader">
              <div className="skeleton-card" />
            </div>
          ) : (
            <div className="station-details-chart">
              {toggleData.map(({ id, icon: Icon, label, desc }) => (
                <div className="station-chart-item" key={id}>
                  <div className="station-chart-icon">
                    <Icon fill="var(--primary-color)" color="var(--primary-color)" />
                  </div>
                  <div className="station-chart-info">
                    <p>{label}</p>
                    <p>{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="station-details-horizontal-line" />

      {/* RIGHT SECTION */}
      <div className="station-details-right">
        {/* Buttons */}
        <div className="station-details-top">
          <MyButton
            label={t('media')}
            buttonType={activeTab === 'media' ? 'submit' : 'primary-outline'}
            onClickFunc={() => setActiveTab('media')}
          />
          <MyButton
            label={t('amenities')}
            buttonType={activeTab === 'amenities' ? 'submit' : 'primary-outline'}
            onClickFunc={() => setActiveTab('amenities')}
          />
        </div>

        {isLoading ? (
          <div className="skeleton-grid-loader">
            {[...Array(5)].map((index) => (
              <div className="skeleton-card" key={index} />
            ))}
          </div>
        ) : (
          <div className="station-details-bottom">
            {activeTab === 'media' && (
              <div className="station-details-bottom">
                {activeTab === 'media' &&
                  (stationDetails?.medias && stationDetails.medias.length > 0 ? (
                    stationDetails.medias.map((media, index) => (
                      <div key={media._id || index} className="image-wrapper">
                        <img
                          src={media.signedUrl || media.url}
                          alt={`${t('station')} ${index}`}
                          onError={(event_) => {
                            const img = event_.currentTarget as HTMLImageElement;
                            img.src = BackGroundImage;
                          }}
                        />
                      </div>
                    ))
                  ) : (
                    <div className="table-no-data-wrap">
                      <img src={NoDataFound} alt="" className="no-data-found-image" />
                      <div className="no-data-found-details">
                        <h3>{t('noDataTitle')}</h3>
                        <p>{t('noDataDescription')}</p>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {activeTab === 'amenities' && (
              <div className="amenity-wrapper">
                <SelectAmenities
                  selectedAmenities={stationDetails?.amenities || []}
                  handleCancel={() => {}}
                  handleStationDetailChange={() => {}}
                  hideButtons
                  hideRemove
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default StationDetails;
